import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { type SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";

import { niapColorFamily } from "utils/color";
import { pluralize } from "utils/stringFormatting";

const CARD_HEIGHT = 125;

interface MailListCardCompactProps {
  title: string;
  email: string;
  description: string;
  memberCount: number;
  id: number;
  categoryColor: string;
}

export default function MailListCardCompact({
  title,
  email,
  description,
  memberCount,
  id,
  categoryColor,
}: MailListCardCompactProps) {
  const navigate = useNavigate();

  const handleView = () => {
    navigate(`details/${id}`);
  };

  const handleDelete = (e: SyntheticEvent) => {
    e.stopPropagation();
    navigate(`delete/${id}`);
  };

  return (
    <Card
      sx={{
        width: "100%",
        height: CARD_HEIGHT,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardActionArea
        sx={{
          display: "flex",
          alignItems: "stretch",
          overflow: "hidden",
          flex: 1, // fill the Card
          "& .delete-button": { opacity: 0, transition: "opacity 0.2s" },
          "&:hover .delete-button, &:focus-within .delete-button": {
            opacity: 1,
          },
        }}
        onClick={handleView}
      >
        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* Delete row */}

          {/* Title row */}
          <Stack direction="row" gap={1} alignItems={"center"}>
            <Typography variant="h6" fontWeight={700} noWrap>
              {title}
            </Typography>

            <Box ml="auto" className="delete-button">
              <IconButton
                sx={{ p: 0, m: 0 }}
                onClick={handleDelete}
                title="Delete Mail List"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Stack>
          <Box>
            <Typography
              noWrap
              sx={{
                color: "primary.main",
                textDecoration: "underline",
                minWidth: 0,
              }}
            >
              {email}
            </Typography>
          </Box>
          {/* Description */}
          <Box>
            <Typography
              sx={{ fontStyle: "italic", color: "text.secondary", minWidth: 0 }}
              noWrap
            >
              {description}
            </Typography>
          </Box>

          {/* Member pill*/}
          <Chip
            label={pluralize(memberCount, "member")}
            color={niapColorFamily(categoryColor)}
            sx={{
              alignSelf: "flex-end",
              mt: "auto",
            }}
          />
        </Box>
      </CardActionArea>
    </Card>
  );
}
